<template>
    <transition name="modal-fade">
        <div v-if="isVisible" class="modal-backdrop" @click.self="close">
            <div class="modal-content" role="dialog" aria-labelledby="modalTitle">
                <header class="modal-header" id="modalTitle">
                    <h2 class="modal-header-title">Informations sur le véhicule à assurer</h2>
                </header>

                <section class="modal-body">
                    <!-- Row 1: Marque, Modèle -->
                    <div class="form-row-grid">
                        <div class="form-group">
                            <label class="form-label">Marque du véhicule <span class="required">*</span></label>
                            <input v-if="category === 'camping-car'" type="text" v-model="localData.marque_porteur"
                                class="redesign-input" placeholder="Ex: FIAT" />
                            <input v-else type="text" v-model="localData.marque_auto" class="redesign-input"
                                placeholder="Ex: RENAULT" />
                        </div>
                        <div class="form-group">
                            <label class="form-label">Modèle du véhicule <span class="required">*</span></label>
                            <input type="text" v-model="localData.modele_porteur" class="redesign-input"
                                placeholder="Ex: CLIO" />
                        </div>
                    </div>

                    <!-- Row 2: Category Specific Primary + Secondary (Cylindrée/Carburant) -->
                    <div class="form-row-grid">
                        <!-- Column 1: Primary Category Field -->
                        <div v-if="category === 'auto'" class="form-group">
                            <label class="form-label">Type de véhicule <span class="required">*</span></label>
                            <select v-model="localData.type_vehicule" class="redesign-select">
                                <option value="0">Choisir un type...</option>
                                <option value="1">Véhicule Particulier (VP)</option>
                                <option value="2">Véhicule Utilitaire (VU)</option>
                                <option value="3">4x4 / TT</option>
                            </select>
                        </div>
                        <div v-if="category === 'moto'" class="form-group">
                            <label class="form-label">Appellation commerciale <span class="required">*</span></label>
                            <input type="text" v-model="localData.app_commerciale" class="redesign-input"
                                placeholder="Ex: ZEPHYR" />
                        </div>
                        <div v-if="category === 'camping-car'" class="form-group">
                            <label class="form-label">PTAC <span class="required">*</span></label>
                            <select v-model="localData.ptc" class="redesign-select">
                                <option v-for="option in ptcOptions" :key="option.value" :value="option.value">
                                    {{ option.name }}
                                </option>
                            </select>
                        </div>

                        <!-- Column 2: Secondary Field (Carburant or Cylindrée) -->
                        <div v-if="category === 'auto'" class="form-group">
                            <label class="form-label">Choisir un carburant <span class="required">*</span></label>
                            <select v-model="localData.carburant" class="redesign-select">
                                <option v-for="(item, index) in list_type_car_carburant" :key="index" :value="index">
                                    {{ item || 'Choisir un carburant...' }}
                                </option>
                            </select>
                        </div>
                        <div v-if="category === 'moto'" class="form-group">
                            <label class="form-label">Choisir une cylindrée <span class="required">*</span></label>
                            <select v-model="localData.cylindree" class="redesign-select">
                                <option v-for="(item, index) in list_cylinders_moto" :key="index" :value="index">
                                    {{ item || "Choisir un type de cylindrée" }}
                                </option>
                            </select>
                        </div>
                        <div v-if="category === 'camping-car' || category === 'auto'" class="form-group">
                            <label class="form-label">Puissance fiscale <span class="required">*</span></label>
                            <input type="text" v-model="localData.puis_fisc" class="redesign-input"
                                placeholder="Ex: 5" />
                        </div>
                    </div>

                    <!-- Row 3: Cylindrée -->

                    <div class="form-row-grid">
                        <div v-if="category === 'auto'" class="form-group">
                            <label class="form-label">Cylindrée <span class="required">*</span></label>
                            <select v-model="localData.cylindree" class="redesign-select">
                                <option v-for="(item, index) in list_type_cylinder" :key="index" :value="index">
                                    {{ item || "Choisir un type de cylindrée" }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Date de 1ère mise en circulation <span
                                    class="required">*</span></label>
                            <div class="form-input-container date-input-container currency-datepicker">
                                <CustomDatepicker :date.sync="circulationDateInputLocal" class="redesign-datepicker" />
                            </div>
                        </div>
                    </div>

                    <!-- Optional: Power KW if needed but hidden from view if not required by UI -->
                    <input v-if="category === 'auto'" type="hidden" v-model="localData.power_kw" />
                    <!-- <input v-if="category === 'auto'" type="hidden" v-model="localData.puis_fisc" /> -->

                    <!-- Row 4: Numéro de série -->
                    <div class="form-row-grid">
                        <div class="form-group full-width">
                            <label class="form-label">Numéro de série (VIN)</label>
                            <input type="text" v-model="localData.num_serie" class="redesign-input"
                                placeholder="Ex: VF1AA000123456789" />
                        </div>
                    </div>
                </section>

                <footer class="modal-footer">

                    <div class="eligibility-action-wrapper">
                        <button type="button" class="btn-secondary-outline" @click="close">Annuler</button>
                        <button type="button" class="btn-primary-outline" :class="{ 'disabled': isVehicleTooOld }"
                            :disabled="isVehicleTooOld" @click="submit">
                            Soumettre
                        </button>

                        <div v-if="isVehicleTooOld" class="eligibility-info-trigger"
                            title="Véhicule non éligible : voir instructions">
                            <font-awesome-icon icon="info-circle" />
                            <div class="eligibility-popover">
                                <div class="popover-title">
                                    <font-awesome-icon icon="exclamation-triangle" />
                                    Informations sur l'éligibilité
                                </div>
                                <p class="popover-desc">Les critères du véhicule ne permettent pas de
                                    souscrire à la garantie choisie (plus de 14 ans).</p>
                                <p class="popover-desc">
                                    Pour effectuer une demande de dérogation, veuillez adresser le
                                    contrôle technique à :
                                    <a
                                        href="mailto:servicetechnique@dynassurances.fr">servicetechnique@dynassurances.fr</a>
                                </p>
                                <div class="popover-desc mb-1 text-left">Informations requises :</div>
                                <ul class="popover-list text-left">
                                    <li>Immatriculation du véhicule</li>
                                    <li>Votre code garage</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    </transition>
</template>

<script>
import garantieFieldsLists from "@/mixins/garantieFieldsLists";
import CustomDatepicker from "@/components/CustomInputs/CustomDatepicker";

export default {
    name: "ManualVehicleEntryModal",
    components: {
        CustomDatepicker
    },
    mixins: [garantieFieldsLists],
    props: {
        isVisible: Boolean,
        initialData: Object,
        category: {
            type: String,
            default: "auto"
        }
    },
    data() {
        const circulationDate = this.initialData.annee_mise_en_circulation && this.initialData.mois_mise_en_circulation && this.initialData.jour_mise_en_circulation
            ? `${this.initialData.annee_mise_en_circulation}-${this.initialData.mois_mise_en_circulation}-${this.initialData.jour_mise_en_circulation}`
            : "";

        return {
            localData: JSON.parse(JSON.stringify(this.initialData || {})),
            circulationDateInputLocal: circulationDate
        };
    },
    computed: {
        ptcOptions() {
            return [
                { value: '0', name: 'Choisir un PTC...' },
                { value: '1', name: 'Inférieur ou égal à 3,5 T' },
                { value: '2', name: 'Supérieur à 3,5 T mais inférieur ou égal à 5 T' },
                { value: '3', name: 'Supérieur à 5 T' },
            ];
        },
        isVehicleTooOld() {

            if (this.category == "camping-car") {
                return false;
            }

            if (!this.localData.annee_mise_en_circulation ||
                !this.localData.mois_mise_en_circulation ||
                !this.localData.jour_mise_en_circulation) return false;

            try {
                const dateMec = this.moment(`${this.localData.annee_mise_en_circulation}-${this.localData.mois_mise_en_circulation}-${this.localData.jour_mise_en_circulation}`, 'YYYY-MM-DD');
                if (!dateMec.isValid()) return false;
                const today = this.moment();
                const age = today.diff(dateMec, 'years');
                return age > 14;
            } catch (e) {
                return false;
            }
        }
    },
    watch: {
        isVisible(newVal) {
            if (newVal) {
                // When showing, reset local data to current state of main component
                this.localData = JSON.parse(JSON.stringify(this.initialData));
                this.circulationDateInputLocal = this.localData.annee_mise_en_circulation && this.localData.mois_mise_en_circulation && this.localData.jour_mise_en_circulation
                    ? `${this.localData.annee_mise_en_circulation}-${this.localData.mois_mise_en_circulation}-${this.localData.jour_mise_en_circulation}`
                    : "";
            }
        },
        circulationDateInputLocal(val) {
            if (val) {
                const date = this.moment(val);
                if (date.isValid()) {
                    this.localData.jour_mise_en_circulation = date.format('DD');
                    this.localData.mois_mise_en_circulation = date.format('MM');
                    this.localData.annee_mise_en_circulation = date.format('YYYY');
                }
            } else {
                this.localData.jour_mise_en_circulation = "";
                this.localData.mois_mise_en_circulation = "";
                this.localData.annee_mise_en_circulation = "";
            }
        }
    },
    methods: {
        close() {
            this.$emit("close");
        },
        submit() {
            this.$emit("submit", this.localData);
        }
    }
};
</script>


// WEBPACK FOOTER //
// src/components/Garantie/shared/ManualVehicleEntryModal.vue